import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    Stack,
    Tooltip,
    Grid,
    Card,
    CardContent,
    Collapse,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    List,
    ListItem,
    ListItemText,
    Divider
} from '@mui/material';
import {
    Visibility,
    Edit,
    Delete,
    Person,
    ShoppingCart,
    LocalShipping,
    ExpandMore,
    ExpandLess,
    LocationOn
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/constants';
import apiClient from '../../api';

const ApparelOrderList = ({ refreshTrigger = 0, prependItem = null }) => {
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [updateData, setUpdateData] = useState({
        status: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    const isAdmin = user?.role === 'admin' || user?.role === 'company_admin';
    const isSubmitter = (order) => order?.submittedBy?._id === user?._id || order?.submittedBy === user?._id;

    // Log screen size changes
    useEffect(() => {
        const handleResize = () => {
            const newWidth = window.innerWidth;
            setScreenWidth(newWidth);
            console.log('[ApparelOrderList] Screen resized:', {
                width: newWidth,
                height: window.innerHeight,
                isMobile: newWidth < 1200,
                isDesktop: newWidth >= 1200,
                breakpoint: newWidth < 600 ? 'xs' : newWidth < 900 ? 'sm' : newWidth < 1200 ? 'md' : newWidth < 1536 ? 'lg' : 'xl'
            });
        };

        console.log('[ApparelOrderList] Initial screen size:', {
            width: window.innerWidth,
            height: window.innerHeight,
            isMobile: window.innerWidth < 1200,
            isDesktop: window.innerWidth >= 1200
        });

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Log when component renders
    useEffect(() => {
        console.log('[ApparelOrderList] Component rendered with:', {
            orderCount: orders.length,
            screenWidth,
            shouldShowMobile: screenWidth < 1200,
            shouldShowDesktop: screenWidth >= 1200
        });
    }, [orders.length, screenWidth]);

    useEffect(() => {
        fetchApparelOrders();
    }, [refreshTrigger]);

    // Prepend new item instantly when provided
    useEffect(() => {
        if (prependItem && prependItem._id && !orders.find(o => o._id === prependItem._id)) {
            setOrders(prev => [prependItem, ...prev]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prependItem]);

    const fetchApparelOrders = async () => {
        try {
            setLoading(true);
            console.log('[ApparelList] GET /forms/apparel');
            const response = await apiClient.get('/forms/apparel');
            console.log('[ApparelList] GET /forms/apparel res', response?.status, response?.data);
            setOrders(response.data.data || []);
        } catch (error) {
            console.error('[ApparelList] Error fetching apparel orders:', error, error?.response?.status, error?.response?.data);
            setError('Failed to load apparel orders');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'submitted':
                return 'info';
            case 'processing':
                return 'warning';
            case 'shipped':
                return 'primary';
            case 'delivered':
                return 'success';
            case 'cancelled':
                return 'error';
            default:
                return 'default';
        }
    };

    const getUrgencyColor = (urgency) => {
        switch (urgency) {
            case 'urgent':
                return 'error';
            case 'rush':
                return 'warning';
            case 'standard':
                return 'default';
            default:
                return 'default';
        }
    };

    const getItemTypeLabel = (type) => {
        const types = {
            'shirt': 'T-Shirt',
            'polo': 'Polo Shirt',
            'hoodie': 'Hoodie',
            'jacket': 'Jacket',
            'vest': 'Safety Vest',
            'hat': 'Hat/Cap',
            'pants': 'Pants',
            'other': 'Other'
        };
        return types[type] || type;
    };

    const calculateTotalItems = (items) => {
        return items.reduce((total, item) => total + item.quantity, 0);
    };

    const handleViewDetails = (order) => {
        setSelectedOrder(order);
        setDetailsDialogOpen(true);
    };

    const handleUpdateOrder = (order) => {
        console.log('[ApparelList] Open update dialog for order', order?._id, order);
        // Prevent owners from editing once processing has started
        if (!isAdmin && isSubmitter(order) && order.status !== 'submitted') {
            setError('Orders can only be edited before processing');
            return;
        }
        setSelectedOrder(order);
        setUpdateData({
            status: order.status,
            items: order.items?.map(it => ({
                shirtTypeItemNumber: it.shirtTypeItemNumber || it.itemType || '',
                shirtColor: it.shirtColor || it.color || '',
                letteringColor: it.letteringColor || 'Black',
                size: it.size || 'M',
                sizeType: it.sizeType || 'Regular',
                quantity: it.quantity || 1,
                notes: it.notes || ''
            })) || []
        });
        setUpdateDialogOpen(true);
    };

    const handleSubmitUpdate = async () => {
        console.log('[ApparelList][handleSubmitUpdate] START');
        console.log('[ApparelList][handleSubmitUpdate] selectedOrder:', selectedOrder);
        console.log('[ApparelList][handleSubmitUpdate] updateData:', updateData);
        console.log('[ApparelList][handleSubmitUpdate] user:', user);
        console.log('[ApparelList][handleSubmitUpdate] isAdmin:', isAdmin);
        console.log('[ApparelList][handleSubmitUpdate] isSubmitter:', isSubmitter(selectedOrder));
        
        if (!selectedOrder) {
            console.log('[ApparelList][handleSubmitUpdate] No selected order, returning');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            console.log('[ApparelList][handleSubmitUpdate] Creating payload...');
            const payload = { ...updateData };
            console.log('[ApparelList][handleSubmitUpdate] Initial payload:', JSON.stringify(payload, null, 2));
            
            // For non-admin submitters, send only items to avoid server rejecting admin fields
            const userIsAdminOrSubmitter = isAdmin || isSubmitter(selectedOrder);
            console.log('[ApparelList][handleSubmitUpdate] userIsAdminOrSubmitter:', userIsAdminOrSubmitter);
            
            if (!userIsAdminOrSubmitter) {
                console.log('[ApparelList][handleSubmitUpdate] User is neither admin nor submitter - should NOT be able to edit');
                // noop
            } else if (!isAdmin) {
                console.log('[ApparelList][handleSubmitUpdate] User is submitter but not admin - sending only items');
                payload.items = updateData.items;
                delete payload.status;
            } else {
                console.log('[ApparelList][handleSubmitUpdate] User is admin - sending all fields');
            }
            
            console.log('[ApparelList][handleSubmitUpdate] Final payload:', JSON.stringify(payload, null, 2));
            console.log('[ApparelList][handleSubmitUpdate] Payload items:', payload.items);
            console.log('[ApparelList][handleSubmitUpdate] Number of items:', payload.items?.length);
            
            // Validate items exist for non-admin users
            if (!isAdmin && (!payload.items || !Array.isArray(payload.items) || payload.items.length === 0)) {
                console.error('[ApparelList][handleSubmitUpdate] ERROR: No items in payload for non-admin user');
                setError('Items are required to update the order');
                setSubmitting(false);
                return;
            }
            
            console.log('[ApparelList][handleSubmitUpdate] About to call apiClient.put...');
            console.log('[ApparelList][handleSubmitUpdate] URL:', `/forms/apparel/${selectedOrder._id}`);
            console.log('[ApparelList][handleSubmitUpdate] apiClient:', apiClient);
            
            const res = await apiClient.put(`/forms/apparel/${selectedOrder._id}`, payload);
            
            console.log('[ApparelList][handleSubmitUpdate] PUT SUCCESS');
            console.log('[ApparelList][handleSubmitUpdate] Response status:', res?.status);
            console.log('[ApparelList][handleSubmitUpdate] Response data:', res?.data);

            // Refresh the list
            console.log('[ApparelList][handleSubmitUpdate] Refreshing orders list...');
            await fetchApparelOrders();
            
            console.log('[ApparelList][handleSubmitUpdate] Closing dialog...');
            setUpdateDialogOpen(false);
            setSelectedOrder(null);
            console.log('[ApparelList][handleSubmitUpdate] COMPLETE');
        } catch (error) {
            console.error('[ApparelList][handleSubmitUpdate] CATCH ERROR');
            console.error('[ApparelList][handleSubmitUpdate] Error object:', error);
            console.error('[ApparelList][handleSubmitUpdate] Error name:', error?.name);
            console.error('[ApparelList][handleSubmitUpdate] Error message:', error?.message);
            console.error('[ApparelList][handleSubmitUpdate] Error code:', error?.code);
            console.error('[ApparelList][handleSubmitUpdate] Error response:', error?.response);
            console.error('[ApparelList][handleSubmitUpdate] Error response status:', error?.response?.status);
            console.error('[ApparelList][handleSubmitUpdate] Error response data:', error?.response?.data);
            console.error('[ApparelList][handleSubmitUpdate] Error request:', error?.request);
            console.error('[ApparelList][handleSubmitUpdate] Error config:', error?.config);
            console.error('[ApparelList][handleSubmitUpdate] Full error:', JSON.stringify(error, null, 2));
            setError(error?.response?.data?.message || 'Failed to update order');
        } finally {
            console.log('[ApparelList][handleSubmitUpdate] FINALLY block');
            setSubmitting(false);
        }
    };

    const handleDeleteOrder = async (orderId) => {
        try {
            setSubmitting(true);
            console.log('[ApparelList] DELETE /forms/apparel/:id', orderId);
            const res = await apiClient.delete(`/forms/apparel/${orderId}`);
            console.log('[ApparelList] DELETE /forms/apparel/:id res', res?.status, res?.data);
            await fetchApparelOrders();
        } catch (error) {
            console.error('[ApparelList] Error deleting order:', error, error?.response?.status, error?.response?.data);
            setError(error?.response?.data?.message || 'Failed to delete order');
        } finally {
            setSubmitting(false);
        }
    };

    const openDeleteConfirm = (order) => {
        setDeleteTarget(order);
        setConfirmDeleteOpen(true);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
            </Box>
        );
    }

    if (orders.length === 0) {
        return (
            <Paper sx={{ p: 1.5, borderRadius: 2, borderTop: '3px solid', borderTopColor: 'primary.main' }}>
                <Typography variant="h6" gutterBottom>
                    Apparel Orders
                </Typography>
                <Alert severity="info">
                    No apparel orders found. Submit your first order above!
                </Alert>
            </Paper>
        );
    }

    const OrderDetailsCard = ({ order }) => {
        const [expanded, setExpanded] = useState(false);

        return (
            <Card>
                <CardContent>
                    <Stack spacing={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Stack spacing={1}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Order #{order._id.slice(-6)}
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Person fontSize="small" />
                                    <Typography variant="body2">
                                        {order.submittedBy?.name}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack spacing={1} alignItems="flex-end">
                                <Chip
                                    label={order.status}
                                    color={getStatusColor(order.status)}
                                    size="small"
                                />
                                <Chip
                                    label={order.urgency}
                                    color={getUrgencyColor(order.urgency)}
                                    size="small"
                                    variant="outlined"
                                />
                            </Stack>
                        </Stack>
                        
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <ShoppingCart fontSize="small" />
                            <Typography variant="body2">
                                {calculateTotalItems(order.items)} items
                            </Typography>
                        </Stack>
                        
                        <Typography variant="body2" color="text.secondary">
                            Submitted: {new Date(order.createdAt).toLocaleString()}
                        </Typography>

                        {order.trackingNumber && (
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <LocalShipping fontSize="small" />
                                <Typography variant="body2">
                                    Tracking: {order.trackingNumber}
                                </Typography>
                            </Stack>
                        )}
                        
                        <Button
                            onClick={() => setExpanded(!expanded)}
                            endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                            size="small"
                        >
                            {expanded ? 'Hide Details' : 'Show Details'}
                        </Button>
                        
                        <Collapse in={expanded}>
                            <Stack spacing={2}>
                                <Divider />
                                <Typography variant="subtitle2">Items:</Typography>
                                <List dense>
                                    {order.items.map((item, index) => (
                                        <ListItem key={index} sx={{ py: 0 }}>
                                            <ListItemText
                                                primary={`${item.shirtTypeItemNumber || 'Shirt'} - Size ${item.size}${item.sizeType && item.sizeType !== 'Regular' ? ` (${item.sizeType})` : ''}`}
                                                secondary={`Shirt Color: ${item.shirtColor}, Lettering: ${item.letteringColor}, Qty: ${item.quantity}${item.notes ? `, Notes: ${item.notes}` : ''}`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                                
                                {/* Shipping address removed per requirements */}

                                {order.specialInstructions && (
                                    <>
                                        <Typography variant="subtitle2">Special Instructions:</Typography>
                                        <Typography variant="body2">{order.specialInstructions}</Typography>
                                    </>
                                )}
                            </Stack>
                        </Collapse>
                        
                        {(isAdmin || isSubmitter(order)) && (
                            <Stack direction="row" spacing={1}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Visibility />}
                                    onClick={() => handleViewDetails(order)}
                                >
                                    Details
                                </Button>
                                {/* Update button with lock for owners when not submitted */}
                                {(!isAdmin && isSubmitter(order) && order.status !== 'submitted') ? (
                                    <Tooltip title="Orders can only be edited before processing">
                                        <span>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<Edit />}
                                                disabled
                                            >
                                                Update
                                            </Button>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<Edit />}
                                        onClick={() => handleUpdateOrder(order)}
                                    >
                                        Update
                                    </Button>
                                )}
                                {(!isAdmin && isSubmitter(order) && order.status !== 'submitted') ? (
                                    <Tooltip title="Only submitted orders can be deleted by the owner">
                                        <span>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                startIcon={<Delete />}
                                                disabled
                                            >
                                                Delete
                                            </Button>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        startIcon={<Delete />}
                                        onClick={() => openDeleteConfirm(order)}
                                    >
                                        Delete
                                    </Button>
                                )}
                            </Stack>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    return (
        <Box>
            <Paper sx={{ p: 1.5, borderRadius: 2, borderTop: '3px solid', borderTopColor: 'primary.main' }}>
                <Typography variant="h6" gutterBottom>
                    Apparel Orders ({orders.length})
                </Typography>
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Mobile-friendly card view for small screens */}
                <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                    {console.log('[ApparelOrderList] RENDERING MOBILE CARD VIEW', { count: orders.length, screenWidth })}
                    <Grid container spacing={2}>
                        {orders.map((order) => (
                            <Grid item xs={12} key={order._id}>
                                <OrderDetailsCard order={order} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Desktop table view */}
                <TableContainer sx={{ display: { xs: 'none', lg: 'block' }, maxHeight: 560 }}>
                    {console.log('[ApparelOrderList] RENDERING DESKTOP TABLE VIEW', { count: orders.length, screenWidth })}
                    <Table stickyHeader>
                        <TableHead sx={{ bgcolor: 'rgba(233, 79, 55, 0.08)' }}>
                            <TableRow>
                                <TableCell>Order ID</TableCell>
                                <TableCell>Submitted By</TableCell>
                                <TableCell>Items</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.map((order) => (
                                <TableRow key={order._id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            #{order._id.slice(-6)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack>
                                            <Typography variant="body2" fontWeight="medium">
                                                {order.submittedBy?.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {order.submittedBy?.email}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {calculateTotalItems(order.items)} items
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={order.status}
                                            color={getStatusColor(order.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {new Date(order.createdAt).toLocaleString()}
                                        </Typography>
                                    </TableCell>
                                    {(isAdmin || isSubmitter(order)) && (
                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewDetails(order)}
                                                    >
                                                        <Visibility />
                                                    </IconButton>
                                                </Tooltip>
                                                {/* Update icon with disabled state for owners when not submitted */}
                                                {(!isAdmin && isSubmitter(order) && order.status !== 'submitted') ? (
                                                    <Tooltip title="Orders can only be edited before processing">
                                                        <span>
                                                            <IconButton size="small" disabled>
                                                                <Edit sx={{ color: 'text.disabled' }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Update Order">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleUpdateOrder(order)}
                                                        >
                                                            <Edit />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {/* Delete icon with disabled state for owners when not submitted */}
                                                {(!isAdmin && isSubmitter(order) && order.status !== 'submitted') ? (
                                                    <Tooltip title="Only submitted orders can be deleted by the owner">
                                                        <span>
                                                            <IconButton size="small" disabled>
                                                                <Delete sx={{ color: 'text.disabled' }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Delete Order">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => openDeleteConfirm(order)}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Details Dialog */}
            <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Order Details - #{selectedOrder?._id.slice(-6)}
                </DialogTitle>
                <DialogContent>
                    {selectedOrder && (
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" gutterBottom>Order Information</Typography>
                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography variant="subtitle2">Submitted By:</Typography>
                                            <Typography variant="body2">{selectedOrder.submittedBy?.name}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2">Status:</Typography>
                                            <Chip
                                                label={selectedOrder.status}
                                                color={getStatusColor(selectedOrder.status)}
                                                size="small"
                                            />
                                        </Box>
                                        {/* Urgency removed per requirements */}
                                        <Box>
                                            <Typography variant="subtitle2">Submitted:</Typography>
                                            <Typography variant="body2">{formatDate(selectedOrder.createdAt)}</Typography>
                                        </Box>
                                        {selectedOrder.trackingNumber && (
                                            <Box>
                                                <Typography variant="subtitle2">Tracking Number:</Typography>
                                                <Typography variant="body2" fontFamily="monospace">
                                                    {selectedOrder.trackingNumber}
                                                </Typography>
                                            </Box>
                                        )}
                                        {selectedOrder.orderTotal && (
                                            <Box>
                                                <Typography variant="subtitle2">Order Total:</Typography>
                                                <Typography variant="body2">${selectedOrder.orderTotal.toFixed(2)}</Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </Grid>
                                
                                {/* Shipping removed */}
                                
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>Items Ordered</Typography>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Shirt Type & Item #</TableCell>
                                                    <TableCell>Size</TableCell>
                                                    <TableCell>Size Type</TableCell>
                                                    <TableCell>Shirt Color</TableCell>
                                                    <TableCell>Lettering</TableCell>
                                                    <TableCell>Quantity</TableCell>
                                                    <TableCell>Notes</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {selectedOrder.items.map((item, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{item.shirtTypeItemNumber || 'Shirt'}</TableCell>
                                                        <TableCell>{item.size}</TableCell>
                                                        <TableCell>{item.sizeType || 'Regular'}</TableCell>
                                                        <TableCell>{item.shirtColor}</TableCell>
                                                        <TableCell>{item.letteringColor}</TableCell>
                                                        <TableCell>{item.quantity}</TableCell>
                                                        <TableCell>{item.notes || 'N/A'}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                                
                                {selectedOrder.specialInstructions && (
                                    <Grid item xs={12}>
                                        <Typography variant="h6" gutterBottom>Special Instructions</Typography>
                                        <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                            <Typography variant="body2">{selectedOrder.specialInstructions}</Typography>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialogOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Update Dialog */}
            <Dialog open={updateDialogOpen} onClose={() => setUpdateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Update Order - #{selectedOrder?._id.slice(-6)}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        {isAdmin && (
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={updateData.status}
                                label="Status"
                                onChange={(e) => setUpdateData(prev => ({ ...prev, status: e.target.value }))}
                            >
                                <MenuItem value="submitted">Submitted</MenuItem>
                                <MenuItem value="processing">Processing</MenuItem>
                                <MenuItem value="shipped">Shipped</MenuItem>
                                <MenuItem value="delivered">Delivered</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                        </FormControl>
                        )}

                        {/* Owner-editable items when status is submitted */}
                        {selectedOrder?.status === 'submitted' && (isAdmin || isSubmitter(selectedOrder)) && (
                            <Stack spacing={2}>
                                <Typography variant="subtitle2">Edit Items</Typography>
                                {updateData.items?.map((it, idx) => (
                                    <Stack key={idx} spacing={1.5} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                        <TextField
                                            label="Shirt Type & Item #"
                                            value={it.shirtTypeItemNumber}
                                            onChange={(e) => {
                                                const items = [...updateData.items];
                                                items[idx].shirtTypeItemNumber = e.target.value;
                                                setUpdateData(prev => ({ ...prev, items }));
                                            }}
                                            placeholder="T-shirt, Hoodie, Long Sleeve (e.g., Gildan Softstyle 64000)"
                                        />
                                        <TextField
                                            label="Shirt Color"
                                            value={it.shirtColor}
                                            onChange={(e) => {
                                                const items = [...updateData.items];
                                                items[idx].shirtColor = e.target.value;
                                                setUpdateData(prev => ({ ...prev, items }));
                                            }}
                                            placeholder="Navy, Red, etc."
                                        />
                                        <FormControl fullWidth>
                                            <InputLabel>Lettering Color</InputLabel>
                                            <Select
                                                label="Lettering Color"
                                                value={it.letteringColor}
                                                onChange={(e) => {
                                                    const items = [...updateData.items];
                                                    items[idx].letteringColor = e.target.value;
                                                    setUpdateData(prev => ({ ...prev, items }));
                                                }}
                                            >
                                                <MenuItem value="Black">Black</MenuItem>
                                                <MenuItem value="White">White</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                            <FormControl fullWidth>
                                                <InputLabel>Size</InputLabel>
                                                <Select
                                                    label="Size"
                                                    value={it.size}
                                                    onChange={(e) => {
                                                        const items = [...updateData.items];
                                                        items[idx].size = e.target.value;
                                                        setUpdateData(prev => ({ ...prev, items }));
                                                    }}
                                                >
                                                    {['XS','S','M','L','XL','XXL','XXXL'].map(s => (
                                                        <MenuItem key={s} value={s}>{s}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <FormControl fullWidth>
                                                <InputLabel>Size Type</InputLabel>
                                                <Select
                                                    label="Size Type"
                                                    value={it.sizeType}
                                                    onChange={(e) => {
                                                        const items = [...updateData.items];
                                                        items[idx].sizeType = e.target.value;
                                                        setUpdateData(prev => ({ ...prev, items }));
                                                    }}
                                                >
                                                    <MenuItem value="Regular">Regular</MenuItem>
                                                    <MenuItem value="Tall">Tall</MenuItem>
                                                    <MenuItem value="Youth">Youth</MenuItem>
                                                </Select>
                                            </FormControl>
                        <TextField
                                                type="number"
                                                label="Quantity"
                                                value={it.quantity}
                                                onChange={(e) => {
                                                    const items = [...updateData.items];
                                                    items[idx].quantity = parseInt(e.target.value) || 1;
                                                    setUpdateData(prev => ({ ...prev, items }));
                                                }}
                                                inputProps={{ min: 1, max: 10 }}
                            fullWidth
                        />
                                        </Stack>
                        <TextField
                                            label="Notes"
                                            value={it.notes}
                                            onChange={(e) => {
                                                const items = [...updateData.items];
                                                items[idx].notes = e.target.value;
                                                setUpdateData(prev => ({ ...prev, items }));
                                            }}
                                            placeholder="Special requests..."
                            fullWidth
                                        />
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUpdateDialogOpen(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitUpdate}
                        variant="contained"
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={20} /> : null}
                    >
                        {submitting ? 'Updating...' : 'Update Order'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete order?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        This action cannot be undone. The order {deleteTarget ? `#${deleteTarget._id.slice(-6)}` : ''} will be permanently deleted.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteOpen(false)} disabled={submitting}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={async () => {
                            if (!deleteTarget) return;
                            await handleDeleteOrder(deleteTarget._id);
                            setConfirmDeleteOpen(false);
                            setDeleteTarget(null);
                        }}
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={20} /> : null}
                    >
                        {submitting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ApparelOrderList; 